import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import { KPI_METRIC_KEYS } from '../../components/social-media/metrics-config'
import type { EntityMetricValue } from '../../components/social-media/metrics-config'
import type { LabelQuery } from '../../components/social-media/types'

interface EntityMetricsGroup {
  key: string
  entityCount: number
  metrics: Record<string, EntityMetricValue>
}

interface EntityMetricsQueryResponse {
  groups: EntityMetricsGroup[]
  totalEntities: number
  /** Set when "Count only posts Sky's Sprinklr table lists" is on: posts in
   * scope outside that table that comments point at (sky_counting.go). */
  postsOutsideSprinklrTable?: number
}

export interface UseSkyEntityMetricsResult {
  /** The single `groupBy: none` bucket, or null while unloaded. */
  metrics: Record<string, EntityMetricValue> | null
  /** Filter-matching entities INCLUDING zero-metric ones — the "N videos" count. */
  totalEntities: number
  /** Posts outside Sky's Sprinklr table that carry comments, shown on their
   * own line; undefined unless the counting switch leaves them out. */
  postsOutsideSprinklrTable?: number
  isLoading: boolean
  /** The previous filter's numbers are on screen while the new ones load. */
  isUpdating: boolean
  error: Error | null
}

/**
 * The KPI strip for one product, in one request.
 *
 * `groupBy: {kind: 'none'}` — one bucket for the whole product, which is what a
 * headline tile means. An empty `groups` still yields `{}` rather than null, so
 * the tiles can render "no data" instead of a permanent skeleton: a department
 * with genuinely no metrics (Sports may be close) must look empty, not broken.
 */
export function useSkyEntityMetrics(
  product: string,
  enabled = true,
  labelQuery?: LabelQuery,
): UseSkyEntityMetricsResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'entity_metrics', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    staleTime: 30000,
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<EntityMetricsQueryResponse>(queryKey),
    queryFn: async ({ signal }): Promise<EntityMetricsQueryResponse> => {
      try {
        const res = await axios.post<EntityMetricsQueryResponse>(
          skyProductURL(product, '/entities/metrics/query'),
          {
            metrics: KPI_METRIC_KEYS.map((metric) => ({ metric })),
            groupBy: { kind: 'none' },
            // Entity-level filters only: this endpoint counts ENTITIES, so a
            // record-level predicate has nothing to apply to here.
            entity: labelQuery?.entity,
          },
          { signal },
        )
        return res.data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  return {
    metrics: query.data ? (query.data.groups[0]?.metrics ?? {}) : null,
    totalEntities: query.data?.totalEntities ?? 0,
    postsOutsideSprinklrTable: query.data?.postsOutsideSprinklrTable,
    isLoading: query.isLoading,
    isUpdating: query.isPlaceholderData,
    error: query.error as Error | null,
  }
}

export default useSkyEntityMetrics
